"use client";

import { CheckIcon, CopyIcon } from "lucide-react";
import { useTheme } from "next-themes";
import type { ComponentProps, HTMLAttributes, ReactNode } from "react";
import { createContext, memo, useContext, useState } from "react";
import {
  oneDark,
  oneLight,
} from "react-syntax-highlighter/dist/esm/styles/prism";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import SyntaxHighlighter from "./json-syntax-highlighter";

type CodeBlockContextType = {
  code: string;
};

const CodeBlockContext = createContext<CodeBlockContextType>({
  code: "",
});

export type CodeBlockProps = HTMLAttributes<HTMLDivElement> & {
  code: string;
  language: string;
  showLineNumbers?: boolean;
  density?: "default" | "compact";
  children?: ReactNode;
};

/** コード表示の用途に応じて、標準と tool 詳細向け compact 密度を切り替える。 */
function CodeBlockComponent({
  code,
  language,
  showLineNumbers = false,
  density = "default",
  className,
  children,
  ...props
}: CodeBlockProps) {
  const { resolvedTheme } = useTheme();
  const isCompact = density === "compact";
  const theme = resolvedTheme === "dark" ? oneDark : oneLight;
  const codeStyle = {
    margin: 0,
    padding: isCompact ? "0.5rem" : "1rem",
    fontSize: isCompact ? "0.6875rem" : "0.875rem",
    lineHeight: isCompact ? "1rem" : "1.25rem",
    background: "hsl(var(--background))",
    color: "hsl(var(--foreground))",
  };

  return (
    <CodeBlockContext.Provider value={{ code }}>
      <div
        className={cn(
          "relative w-full border bg-background text-foreground",
          isCompact
            ? "max-h-48 overflow-auto rounded-sm"
            : "overflow-hidden rounded-md",
          className,
        )}
        {...props}
      >
        <div className="relative">
          <SyntaxHighlighter
            className="overflow-hidden"
            codeTagProps={{
              className: cn(
                "font-mono",
                isCompact ? "text-[11px] leading-4" : "text-sm",
              ),
            }}
            customStyle={codeStyle}
            language={language}
            lineNumberStyle={{
              color: "hsl(var(--muted-foreground))",
              paddingRight: isCompact ? "0.5rem" : "1rem",
              minWidth: isCompact ? "2rem" : "2.5rem",
            }}
            showLineNumbers={showLineNumbers}
            style={theme}
          >
            {code}
          </SyntaxHighlighter>
          {children && (
            <div className="absolute top-2 right-2 flex items-center gap-2">
              {children}
            </div>
          )}
        </div>
      </div>
    </CodeBlockContext.Provider>
  );
}

export const CodeBlock = memo(CodeBlockComponent);

export type CodeBlockCopyButtonProps = ComponentProps<typeof Button> & {
  onCopy?: () => void;
  onError?: (error: Error) => void;
  timeout?: number;
};

export const CodeBlockCopyButton = ({
  onCopy,
  onError,
  timeout = 2000,
  children,
  className,
  ...props
}: CodeBlockCopyButtonProps) => {
  const [isCopied, setIsCopied] = useState(false);
  const { code } = useContext(CodeBlockContext);

  const copyToClipboard = async () => {
    if (typeof window === "undefined" || !navigator.clipboard.writeText) {
      onError?.(new Error("Clipboard API not available"));
      return;
    }

    try {
      await navigator.clipboard.writeText(code);
      setIsCopied(true);
      onCopy?.();
      setTimeout(() => setIsCopied(false), timeout);
    } catch (error) {
      onError?.(error as Error);
    }
  };

  const Icon = isCopied ? CheckIcon : CopyIcon;

  return (
    <Button
      className={cn("shrink-0", className)}
      onClick={copyToClipboard}
      size="icon"
      variant="ghost"
      {...props}
    >
      {children ?? <Icon size={14} />}
    </Button>
  );
};
